import { Loader2 } from "lucide-react";
import type { EmailTemplateOption } from "@/types/email";

type TemplatePickerProps = {
  templates: EmailTemplateOption[];
  loading?: boolean;
  selectedId: string | null;
  onSelect: (template: EmailTemplateOption | null) => void;
};

export function TemplatePicker({ templates, loading, selectedId, onSelect }: TemplatePickerProps) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 aa-glass-card px-4 py-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
        Loading templates…
      </div>
    );
  }

  if (!templates.length) {
    return (
      <div className="aa-glass-card border-dashed border-border/70 px-4 py-5 text-center text-sm text-muted-foreground">
        No templates yet. Build one to speed up outreach.
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      {templates.map((template) => {
        const isSelected = template.id === selectedId;
        return (
          <button
            key={template.id}
            type="button"
            onClick={() => onSelect(isSelected ? null : template)}
            className={`rounded-xl border px-4 py-3 text-left transition-all duration-200 ${
              isSelected
                ? "border-primary/70 bg-primary/10 text-foreground"
                : "border-border bg-background/50 text-foreground/80 hover:border-primary/50"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold">{template.name}</p>
              <div className="flex items-center gap-2">
                {template.variant && (
                  <span className="text-[10px] uppercase tracking-wide text-primary/80">Variant {template.variant}</span>
                )}
                {template.persona && <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{template.persona}</span>}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Subject: {template.subject}</p>
            {template.channel && (
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {template.channel} · v{template.version_id}
              </p>
            )}
          </button>
        );
      })}
    </div>
  );
}
